import { IconX } from '@tabler/icons-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const focusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Dialog dengan fokus terkunci dan Escape untuk menutup.
 *
 * Fokus dipindahkan ke dalam dialog saat dibuka, Tab berputar di dalam dialog,
 * dan fokus kembali ke elemen pemicu saat ditutup. Latar halaman dikunci supaya
 * pembaca layar tidak menjelajah ke belakang dialog.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const node = dialogRef.current
    const first = node?.querySelector<HTMLElement>(focusableSelector)
    ;(first ?? node)?.focus()

    const previousOverflow = document.body.style.overflow
    const root = document.getElementById('root')
    const previousInert = root?.inert ?? false
    if (root) root.inert = true
    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        closeRef.current()
        return
      }
      if (event.key !== 'Tab' || !node) {
        return
      }
      const items = Array.from(node.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (item) => item.offsetParent !== null || item === document.activeElement,
      )
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      if (root) root.inert = previousInert
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [])

  return createPortal(
    <div className="dialog-overlay">
      <div
        ref={dialogRef}
        className={wide ? 'dialog dialog--wide' : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="dialog__head">
          <h2 className="dialog__title" id={titleId}>
            {title}
          </h2>
          <button type="button" className="btn btn--quiet btn--sm" onClick={onClose}>
            <IconX size={18} stroke={1.75} aria-hidden="true" />
            <span className="visually-hidden">Tutup dialog</span>
          </button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer ? <div className="dialog__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
